"use client";

import { useEffect, useMemo, useState } from "react";

const REQUIRED = ["Numpad1", "Numpad5", "Numpad9", "Numpad0"] as const;

type NumLockState = "on" | "off-or-unavailable" | "unknown";

export function KeyboardCheck({ compact = false }: { compact?: boolean }) {
  const [pressed, setPressed] = useState<Set<string>>(new Set());
  const [numLock, setNumLock] = useState<NumLockState>("unknown");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!REQUIRED.includes(event.code as (typeof REQUIRED)[number])) return;
      event.preventDefault();
      setPressed((current) => new Set(current).add(event.code));
      setNumLock(event.getModifierState("NumLock") ? "on" : "off-or-unavailable");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const complete = useMemo(() => REQUIRED.every((code) => pressed.has(code)), [pressed]);

  return (
    <div className={`keyboard-check ${compact ? "compact" : ""} ${complete ? "complete" : ""}`}>
      <div>
        <strong>{complete ? "Numeric keypad verified" : "Verify the numeric keypad"}</strong>
        <span>Press 1, 5, 9, and 0 on the physical numpad. The app records physical key locations and also reports the browser&apos;s Num Lock state.</span>
        {numLock === "on" && <small className="keyboard-status good">Num Lock reported on.</small>}
        {numLock === "off-or-unavailable" && <small className="keyboard-status warning">Num Lock is off or unavailable to this browser. Confirm it before the real assessment.</small>}
      </div>
      <div className="keyboard-check-keys" aria-label="Required numpad keys">
        {REQUIRED.map((code) => {
          const label = code.replace("Numpad", "");
          return <kbd className={pressed.has(code) ? "pressed" : ""} key={code}>{label}</kbd>;
        })}
      </div>
    </div>
  );
}
